import { analyticsManager } from './manager';

let volumeTimer: number | undefined;

function capture(event: string, properties?: Record<string, unknown>) {
  analyticsManager.capture(event, properties);
}

function identify(userId: string, traits?: Record<string, unknown>) {
  analyticsManager.identify(userId, traits);
}

export const analytics = {
  // ---- Identity ----

  identify,

  reset() {
    analyticsManager.reset();
  },
  setPersonProperties(properties: Record<string, unknown>) {
    analyticsManager.setPersonProperties(properties);
  },
  register(properties: Record<string, unknown>) {
    analyticsManager.register(properties);
  },

  // ---- Command Palette ----

  commandPaletteOpened(trigger: 'keyboard' | 'button') {
    capture('command_palette_opened', { trigger });
  },
  commandPaletteClosed(reason: 'escape' | 'overlay' | 'selection' | 'keyboard', query: string) {
    capture('command_palette_closed', { reason, query, had_query: query.length > 0 });
  },
  commandPaletteSearch(query: string, resultCount: number) {
    capture('command_palette_search', { query, result_count: resultCount });
  },
  commandPaletteSelected(itemType: string, itemTitle: string, query: string) {
    capture('command_palette_selected', { item_type: itemType, item_title: itemTitle, query });
  },

  // ---- Content Engagement ----

  articleViewed(slug: string, section: 'blog' | 'prodeng', title: string, readTime: string) {
    capture('article_viewed', { slug, section, title, read_time: readTime });
  },
  articleEditsOpened(slug: string, editCount: number) {
    capture('article_edits_opened', { slug, edit_count: editCount });
  },
  articleScrollDepth(slug: string, depth: 25 | 50 | 75 | 100) {
    capture('article_scroll_depth', { slug, depth });
  },
  articleCompleted(slug: string, timeSpentSeconds: number) {
    capture('article_completed', { slug, time_spent_seconds: timeSpentSeconds });
  },
  articleNavigation(direction: 'prev' | 'next', fromSlug: string, toSlug: string) {
    capture('article_navigation', { direction, from_slug: fromSlug, to_slug: toSlug });
  },
  contentClicked(kind: string, title: string, href: string) {
    capture('content_clicked', { kind, title, href });
  },
  headingAnchorClicked(hash: string) {
    capture('heading_anchor_clicked', { hash });
  },
  footnoteClicked(id: string) {
    capture('footnote_clicked', { footnote_id: id });
  },
  codeCopied(language: string | undefined, length: number) {
    capture('code_copied', { language, length });
  },
  quoteClicked(author: string, textLength: number) {
    capture('quote_clicked', { author, text_length: textLength });
  },
  quoteCopied(author: string, textLength: number) {
    capture('quote_copied', { author, text_length: textLength });
  },

  // ---- Search and Filters ----

  searchExecuted(
    query: string,
    section: 'blog' | 'projects' | 'repos' | 'spotify',
    resultCount: number,
  ) {
    capture('search_executed', { query, section, result_count: resultCount });
  },
  filterApplied(section: 'blog' | 'projects' | 'repos', filterType: string, filterValue: string) {
    capture('filter_applied', { section, filter_type: filterType, filter_value: filterValue });
  },
  filtersCleared(section: 'blog' | 'projects' | 'repos') {
    capture('filters_cleared', { section });
  },
  viewModeChanged(mode: 'grid' | 'list') {
    capture('view_mode_changed', { mode });
  },
  loadMoreClicked(section: string, visibleCount: number) {
    capture('load_more_clicked', { section, visible_count: visibleCount });
  },

  // ---- Music Analyzer ----

  spotifyConnected(email?: string, displayName?: string) {
    if (email) {
      identify(email, { email, display_name: displayName });
    }
    capture('spotify_connected', {
      ...(email && { email }),
      ...(displayName && { display_name: displayName }),
    });
    capture('login_completed', { provider: 'spotify' });
  },
  audioUploadStarted(fileType: string, fileSizeMb: number) {
    capture('audio_upload_started', { file_type: fileType, file_size_mb: fileSizeMb });
  },
  audioUploadRejected(reason: string) {
    capture('audio_upload_rejected', { reason });
  },
  audioAnalysisSubmitted(properties: Record<string, unknown>) {
    capture('audio_analysis_submitted', properties);
  },
  audioAnalysisQueued(properties: Record<string, unknown>) {
    capture('audio_analysis_queued', properties);
  },
  audioAnalysisCacheHit(properties: Record<string, unknown>) {
    capture('audio_analysis_cache_hit', properties);
  },
  audioAnalysisStarted(properties: Record<string, unknown>) {
    capture('audio_analysis_started', properties);
  },
  audioAnalysisCompleted(
    durationSeconds: number,
    hasSpotifyMatch: boolean,
    extra?: Record<string, unknown>,
  ) {
    capture('audio_analysis_completed', {
      duration_seconds: durationSeconds,
      has_spotify_match: hasSpotifyMatch,
      ...extra,
    });
  },
  audioAnalysisFailed(message: string, extra?: Record<string, unknown>) {
    capture('audio_analysis_failed', { error_message: message.slice(0, 300), ...extra });
  },
  trackPlayed(trackName: string, source: 'upload' | 'spotify') {
    capture('track_played', { track_name: trackName, source });
  },
  playbackToggled(action: 'play' | 'pause' | 'resume', trackName?: string) {
    capture('playback_toggled', { action, track_name: trackName });
  },
  playbackSeeked(positionSeconds: number) {
    capture('playback_seeked', { position_seconds: Math.round(positionSeconds) });
  },
  volumeChanged(volume: number) {
    if (typeof window === 'undefined') return;
    window.clearTimeout(volumeTimer);
    volumeTimer = window.setTimeout(() => {
      capture('volume_changed', { volume });
    }, 400);
  },
  playbackDeviceChanged(deviceName: string, deviceType: string) {
    capture('playback_device_changed', { device_name: deviceName, device_type: deviceType });
  },
  spotifyTrackSelected(trackName: string, source: 'search' | 'match' | 'recent') {
    capture('spotify_track_selected', { track_name: trackName, source });
  },
  audioTimelineZoomed(action: 'in' | 'out' | 'reset', zoomLevel: number) {
    capture('audio_timeline_zoomed', { action, zoom_level: Number(zoomLevel.toFixed(2)) });
  },
  modeSwitched(toMode: 'upload' | 'spotify') {
    capture('mode_switched', { to_mode: toMode });
  },
  lastfmTrackClicked(trackName: string, url: string) {
    capture('lastfm_track_clicked', { track_name: trackName, url });
  },
  homeFinderOpened(renderer: 'webgl' | 'fallback') {
    capture('home_finder_opened', { renderer });
  },
  homeFinderFolderSelected(folder: string, source: 'click' | 'query' = 'click') {
    capture('home_finder_folder_selected', { folder, source });
  },
  homeFinderItemOpened(folder: string, slug: string, href: string) {
    capture('home_finder_item_opened', { folder, slug, href });
  },
  homeFinderMusicView(view: 'recents' | 'playlists' | 'liked') {
    capture('home_finder_music_view', { view });
  },
  homeFinderKeyToggled(visible: boolean) {
    capture('home_finder_key_toggled', { visible });
  },
  homeFinderBrowsed(method: 'keys' | 'wheel') {
    capture('home_finder_browsed', { method });
  },

  // ---- Projects ----

  projectViewed(projectId: string, projectTitle: string) {
    capture('project_viewed', { project_id: projectId, project_title: projectTitle });
  },
  projectLinkClicked(projectId: string, linkType: 'github' | 'npm' | 'luarocks' | 'demo' | 'blog') {
    capture('project_link_clicked', { project_id: projectId, link_type: linkType });
  },

  // ---- Uses / 3D scene ----

  sceneItemSelected(category: string) {
    capture('scene_item_selected', { category });
  },
  sceneEditToggled(enabled: boolean) {
    capture('scene_edit_toggled', { enabled });
  },
  sceneFullscreenToggled(enabled: boolean) {
    capture('scene_fullscreen_toggled', { enabled });
  },
  sceneLoaded(hasWebGL: boolean) {
    capture('scene_loaded', { has_webgl: hasWebGL });
  },
  journeyChapterViewed(chapterId: string, label: string) {
    capture('journey_chapter_viewed', { chapter_id: chapterId, chapter_label: label });
  },
  journeyChapterSelected(chapterId: string, label: string, trigger: 'chip' | 'strip') {
    capture('journey_chapter_selected', { chapter_id: chapterId, chapter_label: label, trigger });
  },
  journeyResumeJumped(chapterId: string, label: string) {
    capture('journey_resume_jumped', { chapter_id: chapterId, chapter_label: label });
  },
  journeyDemoOpened(source: 'query') {
    capture('journey_demo_opened', { source });
  },

  // ---- Achievements and Easter Eggs ----

  easterEggDiscovered(eggType: string) {
    capture('easter_egg_discovered', { egg_type: eggType });
  },
  prizeClaimed() {
    capture('prize_claimed');
  },
  achievementUnlocked(achievement: string) {
    capture('achievement_unlocked', { achievement });
  },
  notFoundViewed(path: string) {
    capture('404_viewed', { requested_path: path });
  },

  // ---- Theme ----

  themeChanged(fromMode: string, toMode: string, trigger: 'toggle' | 'palette' | 'system') {
    capture('theme_changed', { from_mode: fromMode, to_mode: toMode, trigger });
    analyticsManager.register({ theme: toMode });
    analyticsManager.setPersonProperties({ theme: toMode });
  },
  retroModeToggled(enabled: boolean) {
    capture('retro_mode_toggled', { enabled });
    analyticsManager.register({ is_retro: enabled });
    analyticsManager.setPersonProperties({ is_retro: enabled });
  },

  // ---- Social / Outbound ----

  outboundLinkClicked(url: string, context: string) {
    capture('outbound_link_clicked', { url, context });
  },
  linkClicked(href: string, kind: 'internal' | 'external' | 'download' | 'mailto' | 'tel' | 'hash', text?: string) {
    capture('link_clicked', { href, kind, text: text?.slice(0, 120) });
  },
  contentCopied(source: string, length: number, preview?: string) {
    capture('content_copied', { source, length, preview });
  },

  // ---- Navigation ----

  navigationClicked(destination: string, source: 'header' | 'mobile-drawer' | 'overflow' | 'footer' = 'header') {
    capture('navigation_clicked', { destination, source });
  },
  drawerToggled(open: boolean) {
    capture('drawer_toggled', { open });
  },
  overflowMenuOpened() {
    capture('overflow_menu_opened');
  },
  searchOpened(trigger: 'keyboard' | 'button') {
    capture('search_opened', { trigger });
  },
  formSubmitted(formId: string, fieldCount: number) {
    capture('form_submitted', { form_id: formId, field_count: fieldCount });
  },
  filesDropped(count: number, types: string[], totalSizeMb: number) {
    capture('files_dropped', { count, types, total_size_mb: totalSizeMb });
  },
  contentPasted(source: string, length: number) {
    capture('content_pasted', { source, length });
  },
  connectionChanged(online: boolean) {
    capture('connection_changed', { online });
  },

  // ---- Auth / SSO ----

  loginStarted(provider: 'google' | 'spotify') {
    capture('login_started', { provider });
  },
  loginCompleted(provider: 'google' | 'spotify') {
    capture('login_completed', { provider });
  },
  logoutClicked(provider: 'google' | 'spotify') {
    capture('logout_clicked', { provider });
  },
  ssoFooterExpanded() {
    capture('sso_footer_expanded');
  },
  ssoFooterDismissed() {
    capture('sso_footer_dismissed');
  },

  // ---- Engagement ----

  pageEngagement(timeMs: number, maxScrollPct: number, clicks: number, keys: number, reason: string) {
    capture('page_engagement', {
      time_ms: timeMs,
      max_scroll_pct: maxScrollPct,
      click_count: clicks,
      key_count: keys,
      reason,
    });
  },
  scrollDepth(depth: 25 | 50 | 75 | 100) {
    capture('scroll_depth', { depth });
  },
  tabHidden(timeVisibleMs: number) {
    capture('tab_hidden', { time_visible_ms: timeVisibleMs });
  },
  tabVisible() {
    capture('tab_visible');
  },
  printInitiated() {
    capture('print_initiated');
  },
  mediaPlayed(tag: string, src: string) {
    capture('media_played', { tag, src: src.slice(0, 200) });
  },
  mediaPaused(tag: string, src: string) {
    capture('media_paused', { tag, src: src.slice(0, 200) });
  },

  // ---- Error Boundary ----

  errorBoundaryHit(errorMessage: string, componentStack: string | undefined, route: string) {
    capture('error_boundary_hit', {
      error_message: errorMessage.slice(0, 500),
      component_stack: componentStack?.slice(0, 1000),
      route,
    });
  },
  captureException(error: Error, properties?: Record<string, unknown>) {
    analyticsManager.captureException(error, properties);
  },

  // ---- Uses Page ----

  usesItemClicked(category: string, item: string, url: string) {
    capture('uses_item_clicked', { category, item, url });
  },
  repoViewed(repoName: string, url: string) {
    capture('repo_viewed', { repo_name: repoName, url });
  },

  // ---- Feature Flags ----

  isFeatureEnabled(flag: string): boolean {
    return analyticsManager.isFeatureEnabled(flag);
  },
  getFeatureFlag(flag: string): string | boolean | undefined {
    return analyticsManager.getFeatureFlag(flag);
  },
};
